// Tree view: folders with nested chats + unfiled section.

import React, { useRef, useEffect, useState, useCallback } from 'react';
import type { ChatSession, ChatFolder } from '../../types';

export function relativeDate(ts: number): string {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

interface FolderTreeProps {
  sessions: ChatSession[];
  folders: ChatFolder[];
  activeSessionId: string | null;
  folderCollapseState: Record<string, boolean>;
  renamingId: string | null;
  renamingType: 'session' | 'folder' | null;
  renameValue: string;
  onToggleFolderCollapse: (folderId: string) => void;
  onSwitchSession: (id: string) => void;
  onStartRenameSession: (id: string, title: string) => void;
  onStartRenameFolder: (id: string, name: string) => void;
  onRenameValueChange: (value: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onChatMenuOpen: (sessionId: string, rect: DOMRect) => void;
  onFolderMenuOpen: (folderId: string, rect: DOMRect) => void;
  onChatDownload: (sessionId: string) => void;
  onCreateFolder: () => void;
  onMoveSessionToFolder: (sessionId: string, folderId: string | undefined) => void;
}

export default function FolderTree({
  sessions,
  folders,
  activeSessionId,
  folderCollapseState,
  renamingId,
  renamingType,
  renameValue,
  onToggleFolderCollapse,
  onSwitchSession,
  onStartRenameSession,
  onStartRenameFolder,
  onRenameValueChange,
  onCommitRename,
  onCancelRename,
  onChatMenuOpen,
  onFolderMenuOpen,
  onChatDownload,
  onCreateFolder,
  onMoveSessionToFolder,
}: FolderTreeProps) {
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Drag-and-drop state
  const [draggedSessionId, setDraggedSessionId] = useState<string | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);

  // Focus rename input when it appears
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  // Group sessions
  const folderMap = new Map(folders.map((f) => [f.id, f]));
  const sessionsByFolder = new Map<string, ChatSession[]>();
  const unfiled: ChatSession[] = [];

  for (const s of sessions) {
    if (s.folderId && folderMap.has(s.folderId)) {
      const arr = sessionsByFolder.get(s.folderId) || [];
      arr.push(s);
      sessionsByFolder.set(s.folderId, arr);
    } else {
      unfiled.push(s);
    }
  }

  // Sort within each group by updatedAt desc
  const sortByUpdate = (a: ChatSession, b: ChatSession) => b.updatedAt - a.updatedAt;
  for (const arr of sessionsByFolder.values()) arr.sort(sortByUpdate);
  unfiled.sort(sortByUpdate);

  // Sort folders by createdAt asc
  const sortedFolders = [...folders].sort((a, b) => a.createdAt - b.createdAt);

  const renderRenameInput = () => (
    <input
      ref={renameInputRef}
      className="malpedia-sidebar-rename-input"
      value={renameValue}
      onChange={(e) => onRenameValueChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommitRename();
        if (e.key === 'Escape') onCancelRename();
      }}
      onBlur={onCommitRename}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const renderChatItem = (session: ChatSession, nested: boolean) => (
    <div
      key={session.id}
      className={`malpedia-sidebar-session-item${session.id === activeSessionId ? ' active' : ''}${nested ? ' nested' : ''}${session.temporary ? ' temporary' : ''}${draggedSessionId === session.id ? ' dragging' : ''}`}
      onClick={() => onSwitchSession(session.id)}
      onDoubleClick={() => onStartRenameSession(session.id, session.title)}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = new DOMRect(e.clientX, e.clientY, 0, 0);
        onChatMenuOpen(session.id, rect);
      }}
      draggable
      onDragStart={(e) => {
        setDraggedSessionId(session.id);
        e.dataTransfer.setData('text/plain', session.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => {
        setDraggedSessionId(null);
        setDragOverTarget(null);
      }}
    >
      {renamingId === session.id && renamingType === 'session' ? (
        renderRenameInput()
      ) : (
        <>
          <span className="malpedia-sidebar-session-title">
            {session.title || (session.temporary ? 'Temporary chat' : 'New chat')}
          </span>
          {session.serverIsSensitive && (
            <span
              className="malpedia-sidebar-session-sensitive"
              title="Marked sensitive — excluded from any future cross-user features"
              aria-label="Sensitive"
            >
              SENSITIVE
            </span>
          )}
          <span className="malpedia-sidebar-session-time">
            {relativeDate(session.updatedAt)}
          </span>
          <button
            className="malpedia-sidebar-session-action"
            onClick={(e) => {
              e.stopPropagation();
              onChatDownload(session.id);
            }}
            aria-label="Download chat"
            title="Download as markdown"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 2v8M4 7l3 3 3-3" />
              <path d="M2.5 11.5h9" />
            </svg>
          </button>
          <button
            className="malpedia-sidebar-session-action"
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              onChatMenuOpen(session.id, rect);
            }}
            aria-label="Chat options"
            title="More options"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor">
              <circle cx="7" cy="3" r="1.2" />
              <circle cx="7" cy="7" r="1.2" />
              <circle cx="7" cy="11" r="1.2" />
            </svg>
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="malpedia-folder-tree">
      {/* Folders */}
      {sortedFolders.map((folder) => {
        const isCollapsed = folderCollapseState[folder.id] ?? false;
        const folderSessions = sessionsByFolder.get(folder.id) || [];
        const count = folderSessions.length;

        return (
          <div key={folder.id} className="malpedia-sidebar-folder-group">
            <div
              className={`malpedia-sidebar-folder-row${dragOverTarget === folder.id ? ' drag-over' : ''}`}
              onClick={() => onToggleFolderCollapse(folder.id)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                onStartRenameFolder(folder.id, folder.name);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const rect = new DOMRect(e.clientX, e.clientY, 0, 0);
                onFolderMenuOpen(folder.id, rect);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDragOverTarget(folder.id);
              }}
              onDragLeave={() => {
                setDragOverTarget((prev) => prev === folder.id ? null : prev);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedSessionId) {
                  onMoveSessionToFolder(draggedSessionId, folder.id);
                  // Auto-expand the folder
                  if (folderCollapseState[folder.id]) {
                    onToggleFolderCollapse(folder.id);
                  }
                }
                setDraggedSessionId(null);
                setDragOverTarget(null);
              }}
            >
              <span className={`malpedia-sidebar-folder-chevron${isCollapsed ? '' : ' expanded'}`}>
                ▶
              </span>
              <span className="malpedia-sidebar-folder-icon">📂</span>
              {renamingId === folder.id && renamingType === 'folder' ? (
                renderRenameInput()
              ) : (
                <span className="malpedia-sidebar-folder-name">{folder.name}</span>
              )}
              <span className="malpedia-sidebar-folder-count">{count}</span>
              <button
                className="malpedia-sidebar-folder-action"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  onFolderMenuOpen(folder.id, rect);
                }}
                aria-label="Folder options"
                title="Folder options"
              >
                <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor">
                  <circle cx="7" cy="3" r="1.2" />
                  <circle cx="7" cy="7" r="1.2" />
                  <circle cx="7" cy="11" r="1.2" />
                </svg>
              </button>
            </div>
            {!isCollapsed && (
              <div
                className="malpedia-sidebar-folder-children"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverTarget(folder.id);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedSessionId) {
                    onMoveSessionToFolder(draggedSessionId, folder.id);
                  }
                  setDraggedSessionId(null);
                  setDragOverTarget(null);
                }}
              >
                {folderSessions.length === 0 ? (
                  <div className="malpedia-sidebar-folder-empty">No chats</div>
                ) : (
                  folderSessions.map((s) => renderChatItem(s, true))
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* New folder button */}
      <button
        className="malpedia-sidebar-new-folder-btn"
        onClick={onCreateFolder}
      >
        + New folder
      </button>

      {/* Unfiled divider + chats */}
      <div
        className={`malpedia-sidebar-unfiled-drop${dragOverTarget === 'unfiled' ? ' drag-over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDragOverTarget('unfiled');
        }}
        onDragLeave={() => {
          setDragOverTarget((prev) => prev === 'unfiled' ? null : prev);
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (draggedSessionId) {
            onMoveSessionToFolder(draggedSessionId, undefined);
          }
          setDraggedSessionId(null);
          setDragOverTarget(null);
        }}
      >
        {unfiled.length > 0 && (
          <>
            <div className="malpedia-sidebar-unfiled-divider">
              <span>Unfiled</span>
            </div>
            {unfiled.map((s) => renderChatItem(s, false))}
          </>
        )}
        {unfiled.length === 0 && draggedSessionId && (
          <div className="malpedia-sidebar-unfiled-divider">
            <span>Drop here to unfile</span>
          </div>
        )}
      </div>
    </div>
  );
}
